import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/Auth/authContext';
import { useClientAccess } from '../../../hooks/useClientAccess';
import { useI18n } from '../../../contexts/I18nContext';
import { deviceTransferService } from '../../../services/deviceTransferService';
import { DeviceTransfer, TransferBox, TransferStatus } from '../../../types/deviceTransfer';
import { isAbortError } from '../../../utils/abortError';

/**
 * The take-over requests the caller is party to: the ones addressed to
 * them (inbox), the ones their client sent (sent), or everything for ANEP.
 * One server-side page per box and status filter.
 */
export const useTransfersList = (refreshTrigger?: number) => {
  const { isAuthenticated } = useAuth();
  const { isAnepStaff } = useClientAccess();
  const { t } = useI18n();

  const [box, setBox] = useState<TransferBox>(isAnepStaff ? 'all' : 'inbox');
  const [statusFilter, setStatusFilter] = useState<TransferStatus | ''>('');
  const [items, setItems] = useState<DeviceTransfer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [localRefresh, setLocalRefresh] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    setCurrentPage(1);
  }, [box, statusFilter]);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    const ac = new AbortController();
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const page = await deviceTransferService.list({ box, status: statusFilter, page: currentPage, limit: itemsPerPage }, ac.signal);
        setItems(page.items);
        setTotal(page.total);
        setTotalPages(page.pages);
      } catch (err: any) {
        if (isAbortError(err)) return;
        console.error('[PORTABILITY] Error fetching requests:', err);
        setItems([]);
        setError(err?.response?.data?.detail || err.message || t('portability.errorLoading'));
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [isAuthenticated, refreshTrigger, localRefresh, box, statusFilter, currentPage, itemsPerPage, t]);

  const facts = useMemo(() => ({
    pending: items.filter((i) => i.status === 'pending').length,
    toDecide: items.filter((i) => i.canDecide).length,
  }), [items]);

  return {
    isAnepStaff, box, setBox, statusFilter, setStatusFilter, items, loading, error, facts,
    currentPage, setCurrentPage, itemsPerPage, setItemsPerPage, total, totalPages,
    reload: () => setLocalRefresh((n) => n + 1),
  };
};

export type TransfersListState = ReturnType<typeof useTransfersList>;
